import { monthKeyOf } from "@couples/shared";

/**
 * 書き出しの名前まわり。
 *
 * Supabase を参照する処理(→ export.ts)とは分けてある。
 * ここだけならそのままテストできる。
 */

export interface ExportItem {
  id: string;
  storage_key: string;
  taken_at: string;
  byte_size: number;
}

export function totalBytesOf(items: readonly ExportItem[]): number {
  return items.reduce((sum, item) => sum + item.byte_size, 0);
}

/**
 * 共有シートに一度に載せられる件数と容量の上限。
 *
 * 共有シートに渡すには実体を全部メモリに載せる必要がある(ZIP のように
 * 出来た端から手放せない)。端末の空きを超えるとタブごと落ちるため、
 * 手前で断る。数字は仕様ではなく安全側に寄せた目安。
 */
export const SHARE_MAX_ITEMS = 50;
export const SHARE_MAX_BYTES = 300 * 1024 * 1024;

/** 共有シートで送れないときの理由。送れるなら null */
export function shareBlockReasonOf(items: readonly ExportItem[]): string | null {
  if (items.length > SHARE_MAX_ITEMS) {
    return `共有シートで保存できるのは${String(SHARE_MAX_ITEMS)}件までです。`;
  }
  if (totalBytesOf(items) > SHARE_MAX_BYTES) {
    return "大きすぎるため共有シートでは保存できません。";
  }
  return null;
}

/** `original.jpg` のような末尾から拡張子を取る。取れなければ bin */
export function extensionOf(storageKey: string): string {
  const name = storageKey.split("/").at(-1) ?? "";
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "bin" : name.slice(dot + 1);
}

/**
 * 拡張子から MIME を戻す(→ apps/api の EXTENSION_BY_MIME の逆引き)。
 *
 * 共有シートは MIME で中身を判断する。`application/octet-stream` のまま渡すと
 * iOS の共有シートに「画像を保存」が出ず、写真アプリへ入れられない。
 * 保存先の Content-Type に頼らず、こちらで付け直す。
 */
export function mimeOf(storageKey: string): string | null {
  switch (extensionOf(storageKey)) {
    case "webp":
      return "image/webp";
    case "jpg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "mp4":
      return "video/mp4";
    case "mov":
      return "video/quicktime";
    default:
      return null;
  }
}

/** 名前に使う撮影日時 `YYYYMMDD_HHMMSS` */
function stampOf(takenAt: string): string {
  // フォルダ名(月)は JST で決まるので、ファイル名の日時も JST に揃える。
  // 片方を UTC にすると、8/1 0時半に撮ったものが 2026-08 の中で 0731 になる
  const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
  return new Date(new Date(takenAt).getTime() + JST_OFFSET_MS)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "_")
    .slice(0, 15);
}

/**
 * 書き出すときのファイル名。
 *
 * 保存先の実体は `original.jpg` のような固定名なので、そのまま使うと
 * 何を落としても同じ名前になる。撮影日時を名前にして見分けられるようにする。
 *
 * 撮影日時だけでは足りず、IDの頭も要る。EXIF の無い写真(スクショや
 * メッセージ経由の画像)と動画はファイルの更新日時に落ちるため
 * (→ lib/media/format.ts の resolveTakenAt)、まとめて選んだものが
 * 同じ秒に揃うことがある。一覧は撮影日時順なので、揃ったものは必ず隣り合って
 * 並び、隣を続けて保存すると名前がぶつかる。
 */
export function fileNameOf(item: Pick<ExportItem, "id" | "storage_key" | "taken_at">): string {
  return `${stampOf(item.taken_at)}_${item.id.slice(0, 8)}.${extensionOf(item.storage_key)}`;
}

/**
 * ZIP の中の置き場所。月ごとのフォルダに入れる。
 *
 * 名前は単体で保存したときと同じにする。ZIP を展開したフォルダと個別に
 * 落としたファイルが混ざっても、同じものが同じ名前で並ぶようにするため。
 */
export function entryPathOf(item: ExportItem): string {
  return `${monthKeyOf(item.taken_at)}/${fileNameOf(item)}`;
}
